/**
 * Wire contracts of the fleet endpoints (ALC-4668, ADR 0032): `/lab` Pending cards, the run page,
 * "Run now" and the rule review tasks. Dates are ISO strings; money is an exact decimal string.
 */

export type FleetAgentModeValue = 'off' | 'shadow' | 'live';
export type FleetRunStatusValue = 'RUNNING' | 'SUCCEEDED' | 'STOPPED_BUDGET' | 'FAILED' | 'SKIPPED';
export type FleetRunTriggerValue = 'SCHEDULED' | 'MANUAL';
export type FleetDecisionOutcomeValue =
  'ACTED' | 'PROPOSED' | 'REJECTED' | 'ACT_FAILED' | 'ACCEPTED';
export type FleetInboxStatusValue = 'OPEN' | 'ACCEPTED' | 'REJECTED' | 'CLOSED';

/**
 * How the `stopReason` of a run closed from outside its own handler starts. The text after the
 * prefix is detail for a person reading logs; the web words the run by its prefix only.
 */
export const FLEET_STOP_REASON_PREFIX = {
  /** The stale sweep closed a run that never finished. */
  abandoned: 'abandoned:',
  /** "Run now" could not hand the run to the worker queue. */
  notQueued: 'not queued:',
  /** The queue handed back a job whose first attempt died; the run is never judged twice. */
  workerLost: 'the worker lost the run',
  /** The worker threw before the run's own handler could close the row. */
  workerError: 'worker error:',
} as const;

export type FleetStopReasonKind = keyof typeof FLEET_STOP_REASON_PREFIX;

/** Which way the platform closed the run, or null for a reason the run wrote itself. */
export function fleetStopReasonKind(stopReason: string | null): FleetStopReasonKind | null {
  if (!stopReason) return null;
  const kinds = Object.keys(FLEET_STOP_REASON_PREFIX) as FleetStopReasonKind[];
  return kinds.find((kind) => stopReason.startsWith(FLEET_STOP_REASON_PREFIX[kind])) ?? null;
}

export interface FleetRunSummaryResponse {
  runId: string;
  agentId: string;
  /** The tenant's mode when the run started. */
  mode: FleetAgentModeValue;
  status: FleetRunStatusValue;
  trigger: FleetRunTriggerValue;
  startedBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  stats: Record<string, unknown>;
  modelCostUsd: string;
  stopReason: string | null;
  /** Proposals this run made that still wait for a person. */
  openProposals: number;
}

/**
 * What a decision was about, in words a person reads (never the stored ids): the agent describes
 * its own candidate. `kind` picks the fallback wording when there is no vendor to name.
 */
export interface FleetDecisionSubjectResponse {
  kind: string;
  /** The vendor (or other counterparty) the candidate concerns. */
  label: string | null;
  amount: string | null;
  currency: string | null;
  /** Date of the movement, `YYYY-MM-DD`. */
  date: string | null;
  /**
   * Where the movement came from: `type` in the agent's words (`BANK_TX`, `CARD_ITEM`), `label`
   * the bank account or card, null when it has no name. Absent for a subject with no source.
   */
  source?: { type: string; label: string | null } | null;
  /** What the movement was matched or proposed with (a bill), when there is one. */
  counterpart?: FleetDecisionCounterpartResponse | null;
}

export interface FleetDecisionCounterpartResponse {
  label: string | null;
  amount: string | null;
  currency: string | null;
  /** `YYYY-MM-DD`. */
  dueDate: string | null;
  /** `YYYY-MM-DD`. */
  issueDate: string | null;
}

export interface FleetDecisionResponse {
  decisionId: string;
  subjectKey: string;
  subject: FleetDecisionSubjectResponse | null;
  outcome: FleetDecisionOutcomeValue;
  reasons: unknown;
  result: unknown;
  actedBy: string | null;
  createdAt: string;
  /** A proposal of this run that still waits for a person (counted in `openProposals`). */
  open: boolean;
  /**
   * The OPEN inbox item a person decides that proposal on: its own card, or the proposed rule
   * that holds it (a reconciliation `rule_pending` settlement). Null when `open` is false.
   */
  decideItemId: string | null;
}

export interface FleetRunDetailResponse extends FleetRunSummaryResponse {
  /** One page of decisions, oldest first; `decisionsNextOffset` asks for the next. */
  decisions: FleetDecisionResponse[];
  /** Every decision the run took, whatever the page. */
  decisionsTotal: number;
  /** The `offset` of the next page, or null on the last one. */
  decisionsNextOffset: number | null;
}

export interface FleetRunListResponse {
  runs: FleetRunSummaryResponse[];
}

/** `GET /fleet/agents/:agentId/status`: what the "Run now" button reads. */
export interface FleetAgentStatusResponse {
  agentId: string;
  mode: FleetAgentModeValue;
  /** The run in progress; "Run now" is disabled while it is set. */
  runningRunId: string | null;
  lastRun: FleetRunSummaryResponse | null;
  /** Who changed the mode last; null while it is the default (shadow) nobody chose. */
  modeChangedBy: { userId: string; name: string } | null;
  modeChangedAt: string | null;
}

/** The modes a person may switch an agent between; turning an agent off or on is not one. */
export const FLEET_AGENT_SWITCHABLE_MODES = ['shadow', 'live'] as const;
export type FleetAgentSwitchableMode = (typeof FLEET_AGENT_SWITCHABLE_MODES)[number];

/**
 * What the reconciliation agent may settle alone in live mode (D14): the agent runs with these caps,
 * and the live-mode confirmation shows them, so the two never say different things.
 */
export const RECONCILIATION_AGENT_CAPS = {
  maxActsPerRun: 200,
  maxActAmount: '5000.00',
  modelBudgetUsd: 2,
} as const;

/**
 * What a person needs, besides `settings:manage`, to turn live mode on: live lets the agent settle
 * reconciliations (`op:manage`) and draft vendor reconciliation rules (`ap:vendor-knowledge:manage`,
 * and `party:read` to read the vendor), so the switch lends the agent no power its caller lacks.
 * Back to shadow takes power away from the agent, so it needs `op:manage` only.
 */
export const FLEET_LIVE_MODE_PERMISSIONS = [
  'op:manage',
  'ap:vendor-knowledge:manage',
  'party:read',
] as const;

/** `PUT /fleet/agents/:agentId/mode`. */
export interface FleetAgentModeRequest {
  mode: FleetAgentSwitchableMode;
}

/**
 * One agent on the "Seus alquimistas" card of `/lab`. Whoever opens `/lab` reads it, so it holds
 * nothing about a company: no run stats, cost or stop reason (those are tenant-wide, on the
 * admin-only run page), and the open proposals count only the cards the caller may read.
 */
export interface FleetAgentOverviewResponse {
  agentId: string;
  mode: FleetAgentModeValue;
  /** A run is in progress now. */
  running: boolean;
  /**
   * The last run, the one in progress while `running`. `runId` opens its page and is sent only to
   * a caller who may open that page (`settings:manage`); null for everyone else.
   */
  lastRun: { startedAt: string; status: FleetRunStatusValue; runId: string | null } | null;
  /** Open proposals the caller may read in `/lab` Pending; null without `op:read`. */
  openProposals: number | null;
}

/** `GET /fleet/agents`: every agent, in registry order. */
export interface FleetAgentListResponse {
  agents: FleetAgentOverviewResponse[];
}

/** `POST /fleet/agents/:agentId/runs`: the run started (it keeps going after the answer). */
export interface FleetRunNowResponse {
  runId: string;
}

export interface FleetInboxItemResponse {
  itemId: string;
  agentId: string;
  runId: string;
  status: FleetInboxStatusValue;
  closedReason: string | null;
  title: string;
  summary: string;
  details: Record<string, unknown> | null;
  /** A card kind the web words itself, with its scalar params; absent for agent-worded cards. */
  kind?: string;
  params?: Record<string, string>;
  createdAt: string;
  /**
   * The movement and the bill the card names, worded as the run page words its decision. Only on
   * `GET /fleet/inbox/items`; null when the agent describes no subject.
   */
  subject?: FleetDecisionSubjectResponse | null;
  /** The reasons of the decision that proposed it (reason codes), as on the run page. */
  reasons?: unknown;
}

export interface FleetInboxItemsResponse {
  items: FleetInboxItemResponse[];
}

export type FleetAcceptResponse =
  | { status: 'accepted' }
  /** The re-check on current data refused it; the item is closed. */
  | { status: 'refused'; reasons: string[] };

/** One reconciliation a rule settled, listed for bulk undo (D10). */
export interface FleetRuleMatchResponse {
  groupId: string;
  decisionId: string;
  decidedAt: string;
  summary: string | null;
  /** Total of the settled movements, in the rule's currency. */
  amount: string | null;
  currencyCode: string;
}

export interface FleetRuleMatchesResponse {
  knowledgeId: string;
  matches: FleetRuleMatchResponse[];
}

export type FleetRuleMatchBlockReason = 'PERIOD_CLOSED' | 'BLOCKED';

export interface FleetRuleUndoResponse {
  /** Group ids undone (open periods). */
  undone: string[];
  /** Matches that stay for a person: a closed period, or another refusal of the undo. */
  listed: { groupId: string; reason: FleetRuleMatchBlockReason; code: string | null }[];
}

export interface FleetRuleReviewResponse {
  knowledgeId: string;
  reviewStatus: 'APPROVED' | 'REJECTED';
}

/** A proposed DRAFT rule as one review card: the settlements it explains are its lines. */
export interface FleetReviewRuleResponse {
  itemId: string;
  vendorName: string;
  currency: string;
  movementSource: 'card' | 'bank';
  /** What the difference is booked as (`IOF`, `JUROS`, ...). */
  bookAs: string;
  /** Amount paid = bill amount × factor, as an exact decimal string (`1.04`). */
  factor: string;
  lineCount: number;
  /**
   * Lines that fit one bill only: the next live run settles them once the rule is accepted, if
   * the run still has room under `FleetReviewResponse.maxActsPerRun`. An upper bound: the forecast
   * counts this rule's lines and those of the rules in effect, not the run's other matches.
   */
  autoCount: number;
  /**
   * Lines that fit one bill only but are over a limit of the run (the per-charge amount, or the
   * matches per run): the next live run proposes each again, for a person to approve.
   */
  approveCount: number;
}

/** A deck proposal, with names its stored candidate does not carry (read at review time). */
export interface FleetReviewProposalResponse extends FleetInboxItemResponse {
  /** The account or card the movement came from ("Itaú Principal · 0123 / 45678-9"). */
  sourceLabel?: string;
  /** The vendor's number of the bill a match pays. */
  billNumber?: string;
  /** A match's bank line or card charge text, read live: the stored candidate never keeps it. */
  movementDescription?: string;
}

/** The `/lab` review deck of an agent: its rules, its other open proposals and what it holds. */
export interface FleetReviewResponse {
  mode: FleetAgentModeValue;
  rules: FleetReviewRuleResponse[];
  /** The open proposals that are neither a rule nor a line of one, newest first (capped). */
  proposals: FleetReviewProposalResponse[];
  proposalTotal: number;
  /** Settlements the agent holds until a rule is decided (a rule's lines). */
  reconcilingCount: number;
  /** Most matches one run settles alone, all rules and matches together; the rest come back. */
  maxActsPerRun: number;
}

export interface FleetRuleLineResponse {
  itemId: string;
  chargeDate: string;
  chargeAmount: string;
  billId: string;
  billDate: string | null;
  billAmount: string;
  gapAmount: string;
  /**
   * `auto`: settles alone if the run still has room (see `FleetReviewRuleResponse.autoCount`).
   * `approve`: fits one bill, but a limit of the run sends it back to a person (`capReason`).
   */
  outcome: 'auto' | 'approve' | 'choose';
  capReason: 'amount_cap' | 'amount_unknown' | 'count_cap' | null;
  otherBill: { billId: string; amount: string; date: string | null } | null;
}

export interface FleetRuleLinesResponse {
  lines: FleetRuleLineResponse[];
  total: number;
  nextOffset: number | null;
}
